'use client';

import React, { useMemo, useState, Suspense, useEffect, useRef, useCallback } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
const cloudflareTurnstileSecretKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SECRET_KEY;
// Form component that uses useSearchParams
function QuoteRequestForm() {
  const searchParams = useSearchParams();
  const selectedItem = searchParams.get('item') || '';
  const selectedType = searchParams.get('type') || '';

  // Anti-spam states
  const [mountTime] = useState(Date.now());
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const renderTurnstile = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).turnstile && turnstileContainerRef.current) {
      // Clear previous widget if any
      if (widgetIdRef.current) {
        (window as any).turnstile.remove(widgetIdRef.current);
      }

      widgetIdRef.current = (window as any).turnstile.render(turnstileContainerRef.current, {
        sitekey: cloudflareTurnstileSecretKey,
        callback: (token: string) => {
          setTurnstileToken(token);
        },
      });
    }
  }, []);

  useEffect(() => {
    // If turnstile is already loaded (e.g. on re-navigation), render it
    if ((window as any).turnstile) {
      renderTurnstile();
    }
  }, [renderTurnstile]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    item: selectedItem,
    type: selectedType
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Honeypot check
    if (honeypot !== "") {
      console.warn("Spam detected: Honeypot filled");
      setSubmitStatus({
        type: 'error',
        message: 'Spam detected. Please try again.'
      });
      return;
    }

    // 2. Time-based check
    const timeTaken = (Date.now() - mountTime) / 1000;
    if (timeTaken < 5) {
      console.warn(`Spam detected: Submitted too quickly (${timeTaken.toFixed(2)}s)`);
      setSubmitStatus({
        type: 'error',
        message: 'Please take a moment to fill the form correctly.'
      });
      return;
    }

    // 3. Turnstile check
    if (!turnstileToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the Cloudflare security check.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Validate required fields
      if (!formData.name || !formData.email || !formData.message) {
        throw new Error('Please fill in all required fields');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please enter a valid email address');
      }
      const payload = {
        Name: formData.name,
        Email: formData.email,
        CompanyName: formData.company,
        ContactNo: formData.phone,
        RequirementMessage: formData.message,
        Item: formData.item,
        Type: formData.type,
        TurnstileToken: turnstileToken, // Added for backend verification later
      }
      // Send email using the mail API
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;

      if (!backendUrl) {
        throw new Error('Backend URL is not configured. Please check your environment variables.');
      }

      const response = await fetch(`${backendUrl}/api/website-quote/submit`, {
        method: 'POST',
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Your quote request has been submitted successfully! We will get back to you soon.'
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          item: selectedItem,
          type: selectedType
        });

      } else {
        throw new Error(result.message || 'Failed to submit quote request');
      }

    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-component-id="src/app/quote-your-request/page.tsx:179:4" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="179" data-component-end-line="323" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-gray-50%22%7D" className="min-h-screen bg-gray-50">
      <div data-component-id="src/app/quote-your-request/page.tsx:180:6" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="180" data-component-end-line="322" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-%5B1400px%5D%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%20py-10%20sm%3Apy-14%20lg%3Apy-16%22%7D" className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
        <div data-component-id="src/app/quote-your-request/page.tsx:181:8" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="181" data-component-end-line="321" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-2xl%20bg-%5B%23edf8ff%5D%20ring-1%20ring-%5B%23cfe8fb%5D%20shadow-%5B0_6px_14px_rgba(0%2C0%2C0%2C0.06)%5D%20p-4%20sm%3Ap-6%20lg%3Ap-8%20%20h-full%22%7D" className="rounded-2xl bg-[#edf8ff] ring-1 ring-[#cfe8fb] shadow-[0_6px_14px_rgba(0,0,0,0.06)] p-4 sm:p-6 lg:p-8  h-full">
          <div data-component-id="src/app/quote-your-request/page.tsx:182:10" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="182" data-component-end-line="319" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-8%20lg%3Agap-12%20items-center%20%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center ">
            {/* Left: Form */}
            <div data-component-id="src/app/quote-your-request/page.tsx:184:12" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="184" data-component-end-line="303" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h2 data-component-id="src/app/quote-your-request/page.tsx:185:14" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="185" data-component-end-line="185" data-component-file="page.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-xl%20sm%3Atext-2xl%20font-semibold%20text-slate-900%20mb-2%20sm%3Amb-3%22%2C%22textContent%22%3A%22Let's%20Know%20Your%20Requirement%22%7D" className="text-xl sm:text-2xl font-semibold text-slate-900 mb-2 sm:mb-3">Let's Know Your Requirement</h2>
              {selectedItem && (
                <p data-component-id="src/app/quote-your-request/page.tsx:187:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="187" data-component-end-line="189" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-slate-600%20mb-5%20sm%3Amb-6%22%2C%22textContent%22%3A%22Selected%20%3A%22%7D" className="text-sm text-slate-600 mb-5 sm:mb-6">
                  Selected {selectedType ? selectedType : 'item'}: <span data-component-id="src/app/quote-your-request/page.tsx:188:67" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="188" data-component-end-line="188" data-component-file="page.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%20text-slate-900%22%7D" className="font-medium text-slate-900">{selectedItem}</span>
                </p>
              )}
              <form data-component-id="src/app/quote-your-request/page.tsx:191:14" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="191" data-component-end-line="302" data-component-file="page.tsx" data-component-name="form" data-component-content="%7B%22elementName%22%3A%22form%22%2C%22className%22%3A%22space-y-4%20sm%3Aspace-y-5%20max-w-xl%22%7D" onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 max-w-xl">
                {/* Status Messages */}
                {submitStatus.type && (
                  <motion.div data-component-id="src/app/quote-your-request/page.tsx:194:18" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="194" data-component-end-line="219" data-component-file="page.tsx" data-component-name="motion.div" data-component-content="%7B%22elementName%22%3A%22motion.div%22%7D"
                    className={`p-4 rounded-lg ${submitStatus.type === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                      }`}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ fontFamily: 'Poppins' }}
                  >
                    <div data-component-id="src/app/quote-your-request/page.tsx:203:20" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="203" data-component-end-line="218" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%22%7D" className="flex items-center">
                      <div data-component-id="src/app/quote-your-request/page.tsx:204:22" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="204" data-component-end-line="214" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%22%7D" className="flex-shrink-0">
                        {submitStatus.type === 'success' ? (
                          <svg data-component-id="src/app/quote-your-request/page.tsx:206:26" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="206" data-component-end-line="208" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22h-5%20w-5%20text-green-400%22%7D" className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                            <path data-component-id="src/app/quote-your-request/page.tsx:207:28" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="207" data-component-end-line="207" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg data-component-id="src/app/quote-your-request/page.tsx:210:26" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="210" data-component-end-line="212" data-component-file="page.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22h-5%20w-5%20text-red-400%22%7D" className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path data-component-id="src/app/quote-your-request/page.tsx:211:28" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="211" data-component-end-line="211" data-component-file="page.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div data-component-id="src/app/quote-your-request/page.tsx:215:22" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="215" data-component-end-line="217" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22ml-3%22%7D" className="ml-3">
                        <p data-component-id="src/app/quote-your-request/page.tsx:216:24" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="216" data-component-end-line="216" data-component-file="page.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20font-medium%22%7D" className="text-sm font-medium">{submitStatus.message}</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <input data-component-id="src/app/quote-your-request/page.tsx:222:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="222" data-component-end-line="230" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22name%22%3A%22name%22%2C%22className%22%3A%22w-full%20rounded-full%20border%20border-%5B%239ed2f5%5D%20bg-white%2F80%20px-4%20py-3%20text-sm%20outline-none%20focus%3Aring-2%20focus%3Aring-%5B%232b8bd3%5D%20focus%3Aborder-%5B%232b8bd3%5D%20placeholder%3Atext-slate-400%20shadow-%5B0_2px_6px_rgba(0%2C0%2C0%2C0.04)%5D%22%7D"
                  type="text"
                  name="name"
                  placeholder="Your Name *"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-full border border-[#9ed2f5] bg-white/80 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#2b8bd3] focus:border-[#2b8bd3] placeholder:text-slate-400 shadow-[0_2px_6px_rgba(0,0,0,0.04)]"
                />
                <input data-component-id="src/app/quote-your-request/page.tsx:231:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="231" data-component-end-line="239" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22email%22%2C%22name%22%3A%22email%22%2C%22className%22%3A%22w-full%20rounded-full%20border%20border-%5B%239ed2f5%5D%20bg-white%2F80%20px-4%20py-3%20text-sm%20outline-none%20focus%3Aring-2%20focus%3Aring-%5B%232b8bd3%5D%20focus%3Aborder-%5B%232b8bd3%5D%20placeholder%3Atext-slate-400%20shadow-%5B0_2px_6px_rgba(0%2C0%2C0%2C0.04)%5D%22%7D"
                  type="email"
                  name="email"
                  placeholder="Your Email ID *"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-full border border-[#9ed2f5] bg-white/80 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#2b8bd3] focus:border-[#2b8bd3] placeholder:text-slate-400 shadow-[0_2px_6px_rgba(0,0,0,0.04)]"
                />
                <input data-component-id="src/app/quote-your-request/page.tsx:240:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="240" data-component-end-line="247" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22name%22%3A%22company%22%2C%22className%22%3A%22w-full%20rounded-full%20border%20border-%5B%239ed2f5%5D%20bg-white%2F80%20px-4%20py-3%20text-sm%20outline-none%20focus%3Aring-2%20focus%3Aring-%5B%232b8bd3%5D%20focus%3Aborder-%5B%232b8bd3%5D%20placeholder%3Atext-slate-400%20shadow-%5B0_2px_6px_rgba(0%2C0%2C0%2C0.04)%5D%22%7D"
                  type="text"
                  name="company"
                  placeholder="Your Company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full rounded-full border border-[#9ed2f5] bg-white/80 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#2b8bd3] focus:border-[#2b8bd3] placeholder:text-slate-400 shadow-[0_2px_6px_rgba(0,0,0,0.04)]"
                />
                <input data-component-id="src/app/quote-your-request/page.tsx:248:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="248" data-component-end-line="255" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22tel%22%2C%22name%22%3A%22phone%22%2C%22className%22%3A%22w-full%20rounded-full%20border%20border-%5B%239ed2f5%5D%20bg-white%2F80%20px-4%20py-3%20text-sm%20outline-none%20focus%3Aring-2%20focus%3Aring-%5B%232b8bd3%5D%20focus%3Aborder-%5B%232b8bd3%5D%20placeholder%3Atext-slate-400%20shadow-%5B0_2px_6px_rgba(0%2C0%2C0%2C0.04)%5D%22%7D"
                  type="tel"
                  name="phone"
                  placeholder="Your Contact no"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full rounded-full border border-[#9ed2f5] bg-white/80 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#2b8bd3] focus:border-[#2b8bd3] placeholder:text-slate-400 shadow-[0_2px_6px_rgba(0,0,0,0.04)]"
                />
                <textarea data-component-id="src/app/quote-your-request/page.tsx:256:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="256" data-component-end-line="264" data-component-file="page.tsx" data-component-name="textarea" data-component-content="%7B%22elementName%22%3A%22textarea%22%2C%22name%22%3A%22message%22%2C%22className%22%3A%22w-full%20rounded-2xl%20border%20border-%5B%239ed2f5%5D%20bg-white%2F80%20px-4%20py-3%20text-sm%20outline-none%20focus%3Aring-2%20focus%3Aring-%5B%232b8bd3%5D%20focus%3Aborder-%5B%232b8bd3%5D%20placeholder%3Atext-slate-400%20shadow-%5B0_2px_6px_rgba(0%2C0%2C0%2C0.04)%5D%20resize-y%22%7D"
                  name="message"
                  placeholder="Share your requirement *"
                  rows={5}
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-2xl border border-[#9ed2f5] bg-white/80 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#2b8bd3] focus:border-[#2b8bd3] placeholder:text-slate-400 shadow-[0_2px_6px_rgba(0,0,0,0.04)] resize-y"
                />

                {/* Honeypot Field (Hidden) */}
                <input data-component-id="src/app/quote-your-request/page.tsx:267:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="267" data-component-end-line="275" data-component-file="page.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22name%22%3A%22website_url%22%2C%22value%22%3A%22%5Bvar%3Ahoneypot%5D%22%7D"
                  type="text"
                  name="website_url"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                {/* Cloudflare Turnstile Widget */}
                <div data-component-id="src/app/quote-your-request/page.tsx:278:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="278" data-component-end-line="283" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-start%22%7D" className="flex justify-start">
                  <div data-component-id="src/app/quote-your-request/page.tsx:279:18" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="279" data-component-end-line="282" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-%5B65px%5D%22%7D"
                    ref={turnstileContainerRef}
                    className="min-h-[65px]" // Reserve space to avoid layout shift
                  ></div>
                </div>

                {/* Script for Turnstile callback */}
                <Script data-component-id="src/app/quote-your-request/page.tsx:286:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="286" data-component-end-line="290" data-component-file="page.tsx" data-component-name="Script" data-component-content="%7B%22elementName%22%3A%22Script%22%2C%22src%22%3A%22https%3A%2F%2Fchallenges.cloudflare.com%2Fturnstile%2Fv0%2Fapi.js%3Frender%3Dexplicit%22%7D"
                  src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
                  strategy="afterInteractive"
                  onLoad={renderTurnstile}
                />

                <button data-component-id="src/app/quote-your-request/page.tsx:292:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="292" data-component-end-line="301" data-component-file="page.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22submit%22%7D"
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto rounded-full text-white text-sm font-medium px-8 py-3 shadow-[0_6px_14px_rgba(43,139,211,0.35)] transition-colors ${isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[#2b8bd3] hover:bg-[#187bc8]'
                    }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            </div>

            {/* Right: Image Card */}
            <div data-component-id="src/app/quote-your-request/page.tsx:306:12" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="306" data-component-end-line="318" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22order-first%20md%3Aorder-none%22%7D" className="order-first md:order-none">
              <div data-component-id="src/app/quote-your-request/page.tsx:307:14" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="307" data-component-end-line="317" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20rounded-xl%20%22%7D" className="relative rounded-xl ">
                <div data-component-id="src/app/quote-your-request/page.tsx:308:16" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="308" data-component-end-line="316" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20aspect-%5B16%2F10%5D%20w-full%22%7D" className="relative aspect-[16/10] w-full">
                  <Image data-component-id="src/app/quote-your-request/page.tsx:309:18" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="309" data-component-end-line="315" data-component-file="page.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2FRequest_a_demo.png%22%2C%22alt%22%3A%22Request%20a%20quote%22%2C%22className%22%3A%22object-cover%20rounded-lg%22%7D"
                    src="/images/Request_a_demo.png"
                    alt="Request a quote"
                    fill
                    className="object-cover rounded-lg"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// Main page component that wraps the form in Suspense
export default function QuoteYourRequest() {
  return (
    <Suspense fallback={
      <div data-component-id="src/app/quote-your-request/page.tsx:331:6" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="331" data-component-end-line="333" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-gray-50%20flex%20items-center%20justify-center%22%7D" className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div data-component-id="src/app/quote-your-request/page.tsx:332:8" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="332" data-component-end-line="332" data-component-file="page.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20text-gray-600%22%2C%22textContent%22%3A%22Loading%20quote%20request%20form...%22%7D" className="animate-pulse text-gray-600">Loading quote request form...</div>
      </div>
    }>
      <QuoteRequestForm data-component-id="src/app/quote-your-request/page.tsx:335:6" data-component-path="src/app/quote-your-request/page.tsx" data-component-line="335" data-component-end-line="335" data-component-file="page.tsx" data-component-name="QuoteRequestForm" data-component-content="%7B%22elementName%22%3A%22QuoteRequestForm%22%7D" />
    </Suspense>
  );
}
